import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * One tile of the features grid: a night-black card in both themes, so the hardware on it reads
 * like a product shot, with the brand colour glowing up behind the picture. The words lead with
 * the point in bold and finish it in the muted tone, one sentence the eye takes in at once.
 */
export const Stage = ({
  id,
  eyebrow,
  title,
  lead,
  body,
  badge,
  side,
  flip,
  fade,
  className,
  artClassName,
  children,
}: {
  id?: string;
  /** A few words over the headline, such as "Step 1". */
  eyebrow?: string;
  /** A headline over the words, for the wide tiles that have the room. */
  title?: string;
  lead: string;
  body: string;
  /** A small label beside the words, such as "Soon". */
  badge?: ReactNode;
  /** Words on the left and the picture on the right from `lg`, for the wide tiles. */
  side?: boolean;
  /** With `side`, the picture on the left instead, so a run of wide tiles can zigzag. */
  flip?: boolean;
  /** Melts the bottom edge into the card, for a phone that runs off it. */
  fade?: boolean;
  className?: string;
  artClassName?: string;
  children: ReactNode;
}) => (
  <article
    id={id}
    className={cn(
      "dark relative isolate flex flex-col overflow-hidden rounded-[32px] bg-[oklch(0.13_0.006_240)] text-foreground ring-1 ring-foreground/[0.07] ring-inset",
      "before:pointer-events-none before:absolute before:inset-x-0 before:bottom-0 before:-z-10 before:h-4/5 before:bg-[radial-gradient(55%_60%_at_50%_100%,color-mix(in_oklch,var(--primary)_24%,transparent),transparent)]",
      side && "lg:flex-row lg:before:bg-[radial-gradient(38%_75%_at_72%_100%,color-mix(in_oklch,var(--primary)_24%,transparent),transparent)]",
      side &&
        flip &&
        "lg:flex-row-reverse lg:before:bg-[radial-gradient(38%_75%_at_28%_100%,color-mix(in_oklch,var(--primary)_24%,transparent),transparent)]",
      id && "scroll-mt-6",
      fade &&
        "after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:z-10 after:h-24 after:bg-linear-to-t after:from-[oklch(0.13_0.006_240)] after:to-transparent",
      className,
    )}
  >
    <div
      className={cn(
        "relative z-10 p-7 md:p-9",
        side && "lg:flex lg:w-[42%] lg:shrink-0 lg:flex-col lg:justify-between lg:gap-10 lg:p-12",
      )}
    >
      {eyebrow ? (
        <p className="mb-4 text-xs font-semibold tracking-[0.18em] text-primary uppercase">{eyebrow}</p>
      ) : null}
      {title ? (
        <h3 className="mb-6 max-w-[14ch] text-[clamp(1.9rem,3.2vw,2.875rem)] leading-[1.02] font-medium tracking-[-0.04em] text-balance lg:mb-0">
          {title}
        </h3>
      ) : null}
      {badge ? <div className="mb-4">{badge}</div> : null}
      <p className="max-w-[44ch] text-[17px] leading-[1.5] text-pretty text-muted-foreground md:text-lg">
        <b className="font-semibold text-foreground">{lead}</b> {body}
      </p>
    </div>
    <div className={cn("relative min-h-0 flex-1", artClassName)}>{children}</div>
  </article>
);

/** The label on a tile for something we are still building. */
export const SoonBadge = ({ children }: { children: ReactNode }) => (
  <span className="inline-flex items-center gap-2 rounded-full bg-foreground/[0.08] px-3 py-1 text-xs font-semibold tracking-[0.14em] text-foreground uppercase ring-1 ring-foreground/10 ring-inset">
    <i aria-hidden className="size-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
    {children}
  </span>
);

/** A frosted chip that floats over a picture: one fact the picture is about. */
export const FloatChip = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span
    aria-hidden
    className={cn(
      "absolute z-20 inline-flex items-center gap-2 rounded-full bg-[oklch(0.2_0.006_240/0.72)] px-3.5 py-2 text-[13px] font-semibold whitespace-nowrap text-white shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6)] ring-1 ring-white/10 backdrop-blur-xl ring-inset",
      className,
    )}
  >
    {children}
  </span>
);
